'use client';

import { DocumentCheckIcon, DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { type ActionOption, SkeletonActionButton } from '~/components/ActionButton';
import { useModal } from '~/components/Modal';
import ConfirmationModal from '~/components/ConfirmationModal';
import { api } from '~/utils/api';
import { serverError } from '~/utils/serverError';
import { toast } from '~/utils/toast';
import useInvalidateScannerAggregates from '~/hooks/useInvalidateScannerAggregates';

type ReportActionsProps = {
  reportId: string;
  isReviewed: boolean;
  isPendingDelete?: boolean;
  /** Called immediately on confirm so the parent can hide the row. */
  onPendingDelete?: (reportId: string) => void;
  /** Called when Undo restores the row, or when a real delete error needs restore. */
  onUndoDelete?: (reportId: string) => void;
  /** Called after a successful commit (or already-deleted) to clear pending state. */
  onDeleteSettled?: (reportId: string) => void;
};

export default function ReportActions({
  reportId,
  isReviewed,
  isPendingDelete = false,
  onPendingDelete,
  onUndoDelete,
  onDeleteSettled,
}: ReportActionsProps) {
  const { mutateAsync: updateScanReport } = api.scanner.updateScanReport.useMutation();
  const { mutateAsync: deleteScanReport } = api.scanner.deleteScanReport.useMutation();
  const utils = api.useUtils();
  const invalidateScannerAggregates = useInvalidateScannerAggregates();
  const [isLoading, setIsLoading] = useState(false);
  const { modalRef, openModal, closeModal } = useModal();

  const handleDelete = () => {
    closeModal();
    onPendingDelete?.(reportId);

    toast.undoable({
      message: 'Report deleted',
      onUndo: () => {
        onUndoDelete?.(reportId);
      },
      onCommit: async () => {
        try {
          setIsLoading(true);
          await deleteScanReport({ id: reportId });
          onDeleteSettled?.(reportId);
          await Promise.all([
            utils.scanner.getScanHistory.invalidate(),
            invalidateScannerAggregates(),
          ]);
        } catch (e) {
          const message = (e as Error).message ?? '';
          if (/not found/i.test(message)) {
            onDeleteSettled?.(reportId);
            await Promise.all([
              utils.scanner.getScanHistory.invalidate(),
              invalidateScannerAggregates(),
            ]);
            return;
          }
          toast.error('Failed to delete report');
          serverError(e as Error);
          onUndoDelete?.(reportId);
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  const options: ActionOption[] = [
    {
      label: isReviewed ? 'Undo mark as reviewed' : 'Mark as reviewed',
      icon: DocumentCheckIcon,
      disabled: isLoading || isPendingDelete,
      onClick: async () => {
        try {
          setIsLoading(true);
          await updateScanReport({ id: reportId, isReviewed: !isReviewed });
          toast.success(isReviewed ? 'Report unmarked as reviewed' : 'Report marked as reviewed');
          await Promise.all([
            utils.scanner.getScanHistory.invalidate(),
            utils.scanner.getScanReport.invalidate({ reportId }),
            invalidateScannerAggregates(),
          ]);
        } catch (e) {
          toast.error('Failed to update report status');
          serverError(e as Error);
        } finally {
          setIsLoading(false);
        }
      },
    },
    {
      label: isReviewed ? 'Unmark all pages as reviewed' : 'Mark all pages as reviewed',
      icon: DocumentDuplicateIcon,
      disabled: isLoading || isPendingDelete,
      onClick: async () => {
        try {
          setIsLoading(true);
          await updateScanReport({
            id: reportId,
            isReviewed: !isReviewed,
            markAllPagesAsReviewed: true,
          });
          toast.success(
            isReviewed ? 'All pages unmarked as reviewed' : 'All pages marked as reviewed',
          );
          await Promise.all([
            utils.scanner.getScanHistory.invalidate(),
            utils.scanner.getScanReport.invalidate({ reportId }),
            invalidateScannerAggregates(),
          ]);
        } catch (e) {
          toast.error('Failed to update pages status');
          serverError(e as Error);
        } finally {
          setIsLoading(false);
        }
      },
    },
    {
      label: 'Delete report',
      icon: TrashIcon,
      disabled: isLoading || isPendingDelete,
      onClick: (e) => {
        e.stopPropagation();
        openModal();
      },
      isWarning: true,
    },
  ];

  return (
    <>
      <SkeletonActionButton options={options} disabled={isPendingDelete} />
      <ConfirmationModal
        modalRef={modalRef}
        onClose={closeModal}
        onConfirm={handleDelete}
        header="Delete Report"
        message="This removes the report from scan history. You can undo from the toast that follows."
        confirmLabel="Delete"
        isLoading={isLoading}
      />
    </>
  );
}
